/**
 * BuildEnvCheck — frontend self-check som verifierar att bundle:n som
 * laddats matchar host-domänen den serveras från.
 *
 * Skydd mot cross-target-deploy: om en stage-bundle hamnar i prod-bucket
 * (incident 2026-05-12) kommer denna check fail:a och blockera UI:t med
 * tydligt felmeddelande istället för att tyst servera fel data till
 * KEMAB-användare.
 *
 * Förutsätter att deploy-aws.sh exporterar REACT_APP_BUILD_ENV vid build
 * (sätts till "production" för --target app, "staging" för --target stage).
 * Mismatch sker bara om bundle byggd för en env hamnar på en annan envs
 * domän — alltså en deploy-bugg.
 *
 * Källa: lessons.md "2026-05-12 — Stage-bundle på prod"
 */

const HOST_TO_EXPECTED_ENV = {
  'app.kanplan.se': 'production',
  'stage.kanplan.se': 'staging',
};

/**
 * Returnerar mismatch-info om bundle inte matchar host, annars null.
 * Returnerar null även för lokal dev (localhost/127.0.0.1/IP) — där
 * vill vi inte blockera utvecklare som testar lokalt.
 */
export function detectBuildEnvMismatch() {
  const buildEnv = process.env.REACT_APP_BUILD_ENV;
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // Saknad build-env = legacy build innan L8 — varna i konsol men blockera ej
  if (!buildEnv) {
    if (hostname in HOST_TO_EXPECTED_ENV) {
      // eslint-disable-next-line no-console
      console.warn(
        '[BuildEnvCheck] REACT_APP_BUILD_ENV saknas i bundle. ' +
        'Ny build krävs för att aktivera env-mismatch-skydd.'
      );
    }
    return null;
  }

  const expectedEnv = HOST_TO_EXPECTED_ENV[hostname];
  if (!expectedEnv) {
    // Okänd hostname (localhost, IP, custom domain) — ingen enforcement
    return null;
  }

  if (buildEnv !== expectedEnv) {
    return {
      hostname,
      buildEnv,
      expectedEnv,
    };
  }
  return null;
}

/**
 * Renderar en helskärm med konfigurationsfel.
 * Tar mismatch-objektet från detectBuildEnvMismatch().
 * Importerar inget från React för att fungera även om React-tree inte
 * har laddats än.
 */
export function renderBuildEnvErrorScreen(mismatch) {
  const root = document.getElementById('root');
  if (!root) return;

  const supportUrl =
    'mailto:mikael.jonasson@kanonaden.com?cc=hej@kanplan.se' +
    '&subject=' + encodeURIComponent('Konfigurationsfel KanPlan – fel bundle på ' + mismatch.hostname) +
    '&body=' + encodeURIComponent(
      'Hej,\n\n' +
      'Jag har öppnat KanPlan och fått ett "Konfigurationsfel"-meddelande.\n\n' +
      '— Teknisk info —\n' +
      'Domän: ' + mismatch.hostname + '\n' +
      'Förväntad miljö: ' + mismatch.expectedEnv + '\n' +
      'Faktiskt bundle: ' + mismatch.buildEnv + '\n' +
      'Webbläsare/OS: ' + (typeof navigator !== 'undefined' ? navigator.userAgent : '') + '\n\n' +
      'Tack på förhand'
    );

  root.innerHTML = (
    '<div style="position:fixed;inset:0;background:#FEF2F2;color:#7F1D1D;' +
    'font-family:Inter,Arial,Helvetica,sans-serif;display:flex;align-items:center;' +
    'justify-content:center;padding:24px;z-index:99999;">' +
      '<div style="max-width:560px;background:#FFFFFF;border:1px solid #FCA5A5;' +
      'border-radius:14px;padding:32px;box-shadow:0 4px 20px rgba(30,41,59,0.10);">' +
        '<div style="width:48px;height:48px;border-radius:14px;background:#FEE2E2;' +
        'display:flex;align-items:center;justify-content:center;margin-bottom:20px;">' +
          '<svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#DC2626" ' +
          'stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M12 9v4"/><path d="M12 17h.01"/>' +
            '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>' +
          '</svg>' +
        '</div>' +
        '<h1 style="font-size:22px;font-weight:700;color:#1E293B;margin:0 0 12px;">' +
          'Konfigurationsfel' +
        '</h1>' +
        '<p style="font-size:14px;color:#475569;line-height:1.6;margin:0 0 16px;">' +
          'KanPlan har laddats med fel konfiguration för den här domänen. ' +
          'För att skydda dig från att se eller skriva fel data har vi blockerat ' +
          'inloggningen.' +
        '</p>' +
        '<p style="font-size:14px;color:#475569;line-height:1.6;margin:0 0 24px;">' +
          '<strong>Inget arbete har sparats på fel ställe.</strong> Detta är en ' +
          'säkerhetsspärr som aktiveras innan något kan gå fel.' +
        '</p>' +
        '<div style="background:#F8FAFC;border:1px solid #E2E8F0;border-radius:8px;' +
        'padding:12px 14px;margin-bottom:24px;font-size:12.5px;color:#475569;">' +
          '<div style="font-weight:600;color:#1E293B;margin-bottom:6px;">Teknisk info</div>' +
          '<div>Domän: <code>' + escapeHtml(mismatch.hostname) + '</code></div>' +
          '<div>Förväntad miljö: <code>' + escapeHtml(mismatch.expectedEnv) + '</code></div>' +
          '<div>Faktiskt bundle: <code>' + escapeHtml(mismatch.buildEnv) + '</code></div>' +
        '</div>' +
        '<a href="' + supportUrl + '" ' +
        'style="display:inline-block;padding:12px 24px;background:var(--kp-accent-fill, #2563EB);color:#FFFFFF;' +
        'border-radius:10px;text-decoration:none;font-size:14px;font-weight:600;">' +
          'Kontakta support' +
        '</a>' +
      '</div>' +
    '</div>'
  );

  // Logga till backend (best-effort, ignore failures eftersom backend kanske inte nås)
  try {
    fetch('/api/log/frontend-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'build_env_mismatch',
        hostname: mismatch.hostname,
        buildEnv: mismatch.buildEnv,
        expectedEnv: mismatch.expectedEnv,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {});
  } catch (e) { /* ignore */ }
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
